import { keepPreviousData, useMutation, useQuery, useQueryClient, type QueryClient } from '@tanstack/react-query';
import {
  attachShippingGuide,
  createOrder,
  fetchOrder,
  listOrders,
  orderQueryFrom,
  updateOrderStatus,
} from '@/api/orders';
import { queryKeys } from '@/lib/query-keys';
import type {
  AttachGuideRequest,
  CreateOrderRequest,
  OrderFilters,
  UpdateOrderStatusRequest,
} from '@/types/orders';

/**
 * Órdenes del negocio activo (módulo 7).
 *
 * El listado y el detalle los lee cualquier miembro del negocio (el AGENTE_HUMANO atiende pedidos);
 * crear una orden manual, cambiar de estado y cargar la guía son solo ADMIN (la pantalla esconde los
 * botones y el backend responde 403 igual si alguien invoca los endpoints a mano).
 *
 * Tres detalles del contrato que se ven acá:
 *  - el listado tiene filtros reales (`estado`, `conversationId`, `limit`), así que las query keys
 *    los incluyen: cambiar de filtro es otra entrada de caché y no una lista mal filtrada;
 *  - con `keepPreviousData` la tabla no se queda en blanco al cambiar de filtro o de límite;
 *  - cargar la guía y crear una orden tocaron la conversación en el backend (lead → CLIENTE,
 *    asistente → POSTVENTA, mensaje saliente con la foto), así que esas mutaciones también invalidan
 *    el inbox: el vendedor que esté mirando el chat ve lo que acaba de pasar.
 */

/** `GET /api/tenants/:tenantId/orders` — últimas órdenes (sin historial: eso es del detalle). */
export function useOrders(tenantId: string | null, filters: OrderFilters = {}) {
  // Los filtros que viajan al backend son los mismos que forman la query key (como en el inbox).
  const query = orderQueryFrom(filters);

  return useQuery({
    queryKey: queryKeys.orders.list(tenantId ?? '', query),
    queryFn: () => listOrders(tenantId ?? '', filters),
    enabled: Boolean(tenantId),
    placeholderData: keepPreviousData,
  });
}

/**
 * `GET /api/tenants/:tenantId/orders/:orderId` — la orden + su historial.
 *
 * Se usa para el detalle (ver `OrderDetailDialog`). Con `orderId` vacío la query no corre: así el
 * diálogo puede montarse cerrado sin disparar una petición.
 */
export function useOrder(tenantId: string | null, orderId: string | null) {
  return useQuery({
    queryKey: queryKeys.orders.detail(tenantId ?? '', orderId ?? ''),
    queryFn: () => fetchOrder(tenantId ?? '', orderId ?? ''),
    enabled: Boolean(tenantId && orderId),
  });
}

/** Invalida los listados (todas las combinaciones de filtros) del negocio activo. */
function invalidateOrderLists(queryClient: QueryClient, tenantId: string | null): void {
  if (tenantId) queryClient.invalidateQueries({ queryKey: queryKeys.orders.lists(tenantId) });
}

/** `POST /api/tenants/:tenantId/orders` (solo ADMIN) — orden manual, nace `PENDIENTE_CONFIRMACION`. */
export function useCreateOrder(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (payload: CreateOrderRequest) => createOrder(tenantId ?? '', payload),
    onSuccess: () => {
      invalidateOrderLists(queryClient, tenantId);
      // Si la orden se vinculó a una conversación, el backend la pasó a postventa y marcó al lead
      // como CLIENTE: el inbox tiene que reflejarlo.
      if (tenantId) {
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all(tenantId) });
      }
    },
  });
}

/**
 * `PATCH .../orders/:orderId/estado` (solo ADMIN).
 *
 * La respuesta trae la orden CON su historial actualizado, así que se escribe directamente en la
 * caché del detalle (el diálogo abierto ve el cambio al instante) además de invalidar el listado,
 * que es el que ordena por fecha y pinta el badge de estado.
 */
export function useUpdateOrderStatus(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      payload,
    }: {
      orderId: string;
      payload: UpdateOrderStatusRequest;
    }) => updateOrderStatus(tenantId ?? '', orderId, payload),
    onSuccess: (order) => {
      invalidateOrderLists(queryClient, tenantId);
      if (tenantId) {
        queryClient.setQueryData(queryKeys.orders.detail(tenantId, order.id), order);
      }
    },
  });
}

/**
 * `PATCH .../orders/:orderId/guia` (solo ADMIN) — guarda la guía, pasa la orden a `ENVIADA` y le
 * manda la foto al cliente.
 *
 * La respuesta trae la orden CON su historial (la carga deja una fila nueva), así que el detalle
 * abierto se actualiza al instante; además se invalidan el listado y el inbox, porque el aviso al
 * cliente es un mensaje saliente en su conversación.
 */
export function useAttachShippingGuide(tenantId: string | null) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      orderId,
      payload,
    }: {
      orderId: string;
      payload: AttachGuideRequest;
    }) => attachShippingGuide(tenantId ?? '', orderId, payload),
    onSuccess: (result, variables) => {
      invalidateOrderLists(queryClient, tenantId);
      if (tenantId) {
        queryClient.setQueryData(queryKeys.orders.detail(tenantId, variables.orderId), result.order);
        // El aviso al cliente es un mensaje saliente en su conversación: el inbox cambia.
        queryClient.invalidateQueries({ queryKey: queryKeys.conversations.all(tenantId) });
      }
    },
  });
}
